import type {
  SoftphoneCallActionCopy,
  SoftphoneCallActionCopyOverrides,
  SoftphoneShortcutAction,
} from 'Modules/softphone/types';

/**
 * What the banner's actions say for a *video* invite, where "Accept" reads
 * wrong and neither tooltip should leave the user guessing what they're joining
 * or turning down. A variant of the call, not a state of the action.
 */
export const VIDEO_INVITE_COPY: SoftphoneCallActionCopyOverrides = {
  accept: { default: { caption: 'Join', tooltip: 'Accept video call' } },
  decline: { default: { tooltip: 'Decline video call' } },
};

/**
 * Why the Video button is (un)available — each entry maps to a
 * `VideoPermission.disabledReason` produced by `useVideoPermission`.
 */
export const VIDEO_TOOLTIPS = {
  externalContact: "You can't video call external contacts",
  conferenceCall: 'Video unavailable in conference calls',
  noProPackage: 'Upgrade to Pro to use video calling',
  enabled: 'Switch to video',
} as const;

/**
 * What each Transfer button *does*, for assistive tech.
 *
 * Both carry the caption "Transfer" and the same chord, so the caption alone
 * leaves a screen reader user unable to tell the grid's Transfer (which opens
 * the panel) from the consultation's (which hands the call over). These name
 * the phase instead.
 *
 * The visible caption stays "Transfer" on both, and WCAG 2.5.3 asks only that
 * the accessible name contain it — which "transfer" still does.
 */
export const TRANSFER_ACTION_NAMES = {
  openPanel: 'Open transfer panel',
  completeWarm: 'Complete warm transfer',
} as const;

/**
 * What the call actions say while a warm transfer consultation is up — the same
 * `default`/`active`/`unavailable` shape as {@link CALL_ACTION_COPY}, keyed by
 * the same actions, but stating only what the consultation changes. Split by
 * which buttons say it, because Transfer appears on both sides.
 */
export const WARM_TRANSFER_COPY: Record<
  'blocked' | 'consultation',
  SoftphoneCallActionCopyOverrides
> = {
  /**
   * The grid's actions, off-limits for the duration: the consultation already
   * owns the transfer flow, and merging would conference the two legs instead
   * of handing the call over.
   */
  blocked: {
    transfer: { unavailable: 'A transfer is already in progress' },
    merge: { unavailable: 'Merge is unavailable during warm transfer' },
  },
  /**
   * The consultation's own buttons. They stand in for Transfer and End — same
   * captions, same chords — so they only restate what has to name the
   * *transfer* rather than the call.
   */
  consultation: {
    transfer: {
      // Not "Transfer call": this hands the call over, it doesn't open the
      // transfer panel. Same words as the accessible name, from one constant.
      default: { tooltip: TRANSFER_ACTION_NAMES.completeWarm },
      active: { caption: 'Transferring...' },
      unavailable: 'Transfer in progress',
    },
    end: { default: { tooltip: 'End warm transfer' } },
  },
};

/**
 * One entry per action, keyed by the same {@link SoftphoneShortcutAction} the
 * button already declares. A test walks SOFTPHONE_SHORTCUTS and asserts every
 * action is captioned here — the `Record` type alone wouldn't catch a missing
 * one, since this project compiles with `strictNullChecks: false`.
 *
 * Each `unavailable` entry is a fallback, used only when nothing more specific
 * applies, so a control that announces itself as unavailable always has a
 * reason to hand to `aria-describedby`. Wording not signed off by Product.
 */
export const CALL_ACTION_COPY: Record<
  SoftphoneShortcutAction,
  SoftphoneCallActionCopy
> = {
  mute: {
    default: { caption: 'Mute' },
    active: { caption: 'Unmute' },
    unavailable: 'Muting is unavailable while the call is not connected',
  },
  hold: {
    default: { caption: 'Hold', tooltip: 'Hold call' },
    active: { caption: 'Resume', tooltip: 'Resume call' },
    unavailable: 'Hold is unavailable while the call is not connected',
  },
  keypad: {
    default: { caption: 'Keypad', tooltip: 'Open keypad' },
    active: { caption: 'Hide', tooltip: 'Hide Keypad' },
    unavailable: 'The keypad is unavailable while the call is not connected',
  },
  // The available tooltip is Video's own "enabled" reason; the disabled ones
  // arrive as a `reason`.
  video: {
    default: { caption: 'Video', tooltip: VIDEO_TOOLTIPS.enabled },
    unavailable: 'Video is unavailable for this call',
  },
  transfer: {
    default: { caption: 'Transfer', tooltip: 'Transfer call' },
    unavailable: 'Transfer is unavailable for this call',
  },
  merge: {
    default: { caption: 'Merge', tooltip: 'Merge calls' },
    // No tooltip of its own: mid-merge the tooltip just repeats "Merging...".
    active: { caption: 'Merging...' },
    unavailable: 'Merge allowed for two or more calls',
  },
  end: {
    default: { caption: 'Hang up', tooltip: 'End Call' },
    unavailable: 'There is no call to end',
  },
  // Audio wording. A video invite is the same action on the same chord, so its
  // variant lives in VIDEO_INVITE_COPY rather than in a second entry here.
  accept: { default: { caption: 'Accept', tooltip: 'Accept call' } },
  decline: { default: { caption: 'Decline', tooltip: 'Decline call' } },
};
